import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth, API } from '../context/AuthContext';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Shield, Users, Package, MessageSquare, Upload, Sparkles, Settings, FileText,
  Loader2, CheckCircle, AlertCircle, BarChart3, Plus, Pencil, Trash2, Eye, Search
} from 'lucide-react';
import { toast } from 'sonner';

export default function AdminPage() {
  const { authHeaders } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [xmlFile, setXmlFile] = useState(null);
  const [xmlUrl, setXmlUrl] = useState('');
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [batchCount, setBatchCount] = useState(50);
  const [batchLoading, setBatchLoading] = useState(false);
  const [batchResult, setBatchResult] = useState(null);
  const [grokKey, setGrokKey] = useState('');
  const [xmlImports, setXmlImports] = useState([]);
  const [articles, setArticles] = useState([]);
  const [smtpHost, setSmtpHost] = useState('');
  const [smtpPort, setSmtpPort] = useState('587');
  const [smtpUser, setSmtpUser] = useState('');
  const [smtpPass, setSmtpPass] = useState('');
  const [smtpSaving, setSmtpSaving] = useState(false);
  const [smtpConfigured, setSmtpConfigured] = useState(false);
  const [smtpTestEmail, setSmtpTestEmail] = useState('');
  const [epidemosKey, setEpidemosKey] = useState('');
  const [seoStats, setSeoStats] = useState(null);
  const [seoCount, setSeoCount] = useState(10);
  const [seoGenerating, setSeoGenerating] = useState(false);
  const [seoRegenerating, setSeoRegenerating] = useState(false);
  const [seoResult, setSeoResult] = useState(null);
  const [seoJob, setSeoJob] = useState(null);
  const [adminProducts, setAdminProducts] = useState([]);
  const [adminProductsTotal, setAdminProductsTotal] = useState(0);
  const [adminProductsPage, setAdminProductsPage] = useState(1);
  const [adminProductsFilter, setAdminProductsFilter] = useState('all');
  const [adminProductsSearch, setAdminProductsSearch] = useState('');

  useEffect(() => {
    fetchStats();
    fetchImports();
    fetchArticles();
    fetchSmtpConfig();
    fetchSeoStats();
  }, []);

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API}/admin/stats`, { headers: authHeaders() });
      setStats(res.data);
    } catch (err) {
      toast.error('Brak uprawnień admina');
    }
    setLoading(false);
  };

  const fetchArticles = async () => {
    try {
      const res = await axios.get(`${API}/blog/articles`, { params: { limit: 50, status: null } });
      setArticles(res.data.articles);
    } catch {}
  };

  const deleteArticle = async (id) => {
    if (!window.confirm('Usunąć artykuł?')) return;
    try {
      await axios.delete(`${API}/blog/articles/${id}`, { headers: authHeaders() });
      toast.success('Artykuł usunięty');
      fetchArticles();
    } catch { toast.error('Błąd usuwania'); }
  };

  const fetchImports = async () => {
    try {
      const res = await axios.get(`${API}/admin/xml-imports`, { headers: authHeaders() });
      setXmlImports(res.data.imports);
    } catch {}
  };

  const fetchSmtpConfig = async () => {
    try {
      const res = await axios.get(`${API}/admin/smtp-config`, { headers: authHeaders() });
      setSmtpConfigured(res.data.configured);
      if (res.data.config) {
        setSmtpHost(res.data.config.host || '');
        setSmtpPort(String(res.data.config.port || 587));
        setSmtpUser(res.data.config.user || '');
      }
    } catch {}
  };

  const saveSmtpConfig = async () => {
    if (!smtpHost || !smtpUser || !smtpPass) { toast.error('Wypełnij wszystkie pola SMTP'); return; }
    setSmtpSaving(true);
    try {
      await axios.post(`${API}/admin/smtp-config`, {
        host: smtpHost, port: parseInt(smtpPort), user: smtpUser, password: smtpPass
      }, { headers: authHeaders() });
      toast.success('SMTP zapisane!');
      setSmtpConfigured(true);
      setSmtpPass('');
    } catch (err) { toast.error(err.response?.data?.detail || 'Błąd zapisu'); }
    setSmtpSaving(false);
  };

  const testSmtp = async () => {
    if (!smtpTestEmail) { toast.error('Podaj email testowy'); return; }
    try {
      const formData = new FormData();
      formData.append('email', smtpTestEmail);
      const res = await axios.post(`${API}/admin/smtp-test`, formData, { headers: authHeaders() });
      toast.success(res.data.message);
    } catch (err) { toast.error(err.response?.data?.detail || 'Błąd testu SMTP'); }
  };

  const fetchSeoStats = async () => {
    try {
      const res = await axios.get(`${API}/admin/seo-stats`, { headers: authHeaders() });
      setSeoStats(res.data);
    } catch {}
    // Auto-check if job is running
    try {
      const jobRes = await axios.get(`${API}/admin/seo-job-status`, { headers: authHeaders() });
      setSeoJob(jobRes.data.job);
      if (jobRes.data.job?.status === 'running') {
        pollSeoJob();
      }
    } catch {}
  };

  const generateSeo = async () => {
    setSeoGenerating(true); setSeoResult(null);
    try {
      const res = await axios.post(`${API}/admin/generate-seo?count=${seoCount}`, null, { headers: authHeaders() });
      setSeoResult(res.data);
      if (res.data.status === 'started') { toast.success('Generowanie uruchomione w tle!'); pollSeoJob(); }
      else if (res.data.status === 'already_running') { toast.info('Generowanie już trwa'); pollSeoJob(); }
      else { toast.success(`Wygenerowano ${res.data.processed} opisów SEO!`); fetchSeoStats(); }
    } catch (err) { toast.error(err.response?.data?.detail || 'Błąd'); }
  };

  const regenerateSeo = async () => {
    setSeoRegenerating(true); setSeoResult(null);
    try {
      const res = await axios.post(`${API}/admin/regenerate-seo?count=${seoCount}`, null, { headers: authHeaders() });
      setSeoResult(res.data);
      if (res.data.status === 'started') { toast.success('Regeneracja uruchomiona w tle!'); pollSeoJob(); }
      else if (res.data.status === 'already_running') { toast.info('Generowanie już trwa'); pollSeoJob(); }
      else { toast.success(`Zregenerowano ${res.data.processed} opisów SEO!`); fetchSeoStats(); }
    } catch (err) { toast.error(err.response?.data?.detail || 'Błąd'); }
    setSeoRegenerating(false);
  };

  const pollSeoJob = () => {
    const interval = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/admin/seo-job-status`, { headers: authHeaders() });
        setSeoJob(res.data.job);
        if (res.data.job?.status !== 'running') {
          clearInterval(interval);
          fetchSeoStats();
          setSeoGenerating(false);
          setSeoRegenerating(false);
        }
      } catch { clearInterval(interval); }
    }, 3000);
  };

  const fetchAdminProducts = async () => {
    try {
      const params = { page: adminProductsPage, limit: 15, filter: adminProductsFilter };
      if (adminProductsSearch) params.search = adminProductsSearch;
      const res = await axios.get(`${API}/admin/products`, { params, headers: authHeaders() });
      setAdminProducts(res.data.products);
      setAdminProductsTotal(res.data.total);
    } catch {}
  };

  const handleXmlImport = async () => {
    if (!xmlFile && !xmlUrl) { toast.error('Wybierz plik XML lub podaj URL'); return; }
    setImporting(true);
    setImportResult(null);
    try {
      let res;
      if (xmlUrl) {
        const encodedUrl = encodeURIComponent(xmlUrl);
        res = await axios.get(`${API}/admin/xml-import-url?xml_url=${encodedUrl}`, { headers: authHeaders() });
      } else {
        const formData = new FormData();
        formData.append('file', xmlFile);
        res = await axios.post(`${API}/admin/xml-import`, formData, {
          headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' }
        });
      }
      setImportResult(res.data);
      toast.success(`Zaimportowano ${res.data.imported} okazji!`);
      fetchStats();
      fetchImports();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Błąd importu');
    }
    setImporting(false);
  };

  const handleBatchAi = async () => {
    setBatchLoading(true);
    setBatchResult(null);
    try {
      const res = await axios.post(`${API}/admin/batch-ai?count=${batchCount}`, null, { headers: authHeaders() });
      setBatchResult(res.data);
      toast.success(`Wygenerowano ${res.data.processed} opisów AI!`);
      fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Błąd batch AI');
    }
    setBatchLoading(false);
  };

  const saveGrokKey = async () => {
    try {
      const formData = new FormData();
      formData.append('key', 'grok_api_key');
      formData.append('value', grokKey);
      await axios.post(`${API}/admin/settings`, formData, { headers: authHeaders() });
      toast.success('Klucz Grok API zapisany');
      setGrokKey('');
    } catch {
      toast.error('Błąd zapisu');
    }
  };

  if (loading) return (
    <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-primary animate-spin" /></div>
  );

  return (
    <div className="max-w-4xl mx-auto px-4 py-6 pb-20 md:pb-6" data-testid="admin-page">
      <div className="flex items-center gap-2 mb-6">
        <Shield className="w-6 h-6 text-primary" />
        <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight text-white">
          PANEL ADMINA
        </h1>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6" data-testid="admin-stats">
          {[
            { icon: Users, label: 'Użytkownicy', val: stats.users_count },
            { icon: Package, label: 'Okazje', val: stats.deals_count },
            { icon: MessageSquare, label: 'Komentarze', val: stats.comments_count },
            { icon: FileText, label: 'Import XML', val: stats.xml_deals },
            { icon: Sparkles, label: 'Opisy AI', val: stats.ai_deals },
          ].map(({ icon: Icon, label, val }) => (
            <div key={label} className="bg-card border border-border/40 p-4 flex flex-col items-center text-center">
              <Icon className="w-5 h-5 text-primary mb-1" />
              <span className="font-heading text-xl font-bold text-white">{val}</span>
              <span className="text-xs text-muted-foreground">{label}</span>
            </div>
          ))}
        </div>
      )}

      <Tabs defaultValue="xml" className="space-y-4">
        <TabsList className="bg-secondary flex-wrap">
          <TabsTrigger value="xml" className="font-heading uppercase tracking-wider text-xs">Import XML</TabsTrigger>
          <TabsTrigger value="seo" className="font-heading uppercase tracking-wider text-xs">Generuj SEO</TabsTrigger>
          <TabsTrigger value="products" className="font-heading uppercase tracking-wider text-xs" onClick={() => fetchAdminProducts()}>Produkty</TabsTrigger>
          <TabsTrigger value="ai" className="font-heading uppercase tracking-wider text-xs">Batch AI</TabsTrigger>
          <TabsTrigger value="blog" className="font-heading uppercase tracking-wider text-xs">Blog</TabsTrigger>
          <TabsTrigger value="settings" className="font-heading uppercase tracking-wider text-xs">Ustawienia</TabsTrigger>
          <TabsTrigger value="users" className="font-heading uppercase tracking-wider text-xs">Użytkownicy</TabsTrigger>
        </TabsList>

        {/* SEO Generator */}
        <TabsContent value="seo">
          <div className="bg-card border border-border/40 p-6 space-y-4">
            <h2 className="font-heading text-lg font-bold text-white">Generator opisów SEO 2026</h2>
            <p className="text-sm text-muted-foreground">
              AI wygeneruje profesjonalne opisy z: tabelą specyfikacji, nagłówkami H2/H3, listami, FAQ (8 pytań) i podpisem eksperta Damiana.
            </p>

            {seoStats && (
              <div className="flex flex-wrap gap-3 mb-4">
                <span className="px-3 py-1 text-xs font-heading font-bold border border-primary/30 text-primary">{seoStats.total} produktów</span>
                <span className="px-3 py-1 text-xs font-heading font-bold border border-green-500/30 text-green-400">{seoStats.with_seo} z opisem SEO</span>
                <span className="px-3 py-1 text-xs font-heading font-bold border border-destructive/30 text-destructive">{seoStats.without_seo} bez opisu</span>
              </div>
            )}

            <div className="bg-secondary/50 border border-border/40 p-4">
              <Label className="text-sm text-muted-foreground mb-2 block">Ilość do wygenerowania:</Label>
              <div className="flex gap-2 mb-2">
                {[10, 20, 50, 100, 200].map(n => (
                  <button key={n} onClick={() => setSeoCount(n)}
                    className={`px-4 py-1.5 text-sm font-heading border transition-colors ${
                      seoCount === n ? 'border-primary text-primary bg-primary/10' : 'border-border/40 text-muted-foreground'
                    }`} data-testid={`seo-count-${n}`}>{n} szt.</button>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">Szacowany czas: ~{Math.ceil(seoCount * 15 / 60)} min ({seoCount} x ~15 sek/produkt)</p>
            </div>

            <div className="border-2 border-primary/30 bg-primary/5 p-5">
              <p className="text-xs text-muted-foreground mb-3">Generuje opisy SEO dla produktów, które ich nie mają ({seoStats?.without_seo || 0} oczekujących)</p>
              <Button onClick={generateSeo} disabled={seoGenerating || (seoJob && seoJob.status === 'running')}
                className="w-full bg-primary text-primary-foreground rounded-none font-heading uppercase text-sm h-12" data-testid="generate-seo-btn">
                {seoGenerating || (seoJob && seoJob.status === 'running') ? (
                  <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Generowanie w toku...</>
                ) : (
                  <><Sparkles className="w-4 h-4 mr-2" /> Generuj {seoCount} opisów SEO</>
                )}
              </Button>
            </div>

            {/* Job Progress */}
            {seoJob && seoJob.status === 'running' && (
              <div className="bg-primary/5 border border-primary/30 p-4 animate-pulse" data-testid="seo-job-progress">
                <div className="flex items-center gap-2 mb-2">
                  <Loader2 className="w-4 h-4 text-primary animate-spin" />
                  <span className="font-heading font-bold text-primary text-sm">Generowanie w toku...</span>
                </div>
                <div className="flex items-center gap-3 text-xs text-muted-foreground">
                  <span>Przetworzono: <strong className="text-white">{seoJob.processed}</strong> / {seoJob.total || seoJob.count}</span>
                  <span>Błędy: <strong className="text-destructive">{seoJob.errors}</strong></span>
                </div>
                <div className="w-full h-2 bg-secondary mt-2 overflow-hidden">
                  <div className="h-full bg-primary transition-all duration-500" style={{ width: `${seoJob.total ? (seoJob.processed / seoJob.total) * 100 : 0}%` }} />
                </div>
              </div>
            )}

            {seoJob && seoJob.status === 'completed' && !seoResult && (
              <div className="bg-secondary/50 border border-green-500/30 p-4" data-testid="seo-job-done">
                <div className="flex items-center gap-2 text-green-400 mb-1">
                  <CheckCircle className="w-4 h-4" />
                  <span className="font-heading font-bold">Generowanie zakończone!</span>
                </div>
                <p className="text-sm text-muted-foreground">
                  Przetworzono: <strong className="text-white">{seoJob.processed}</strong> | Błędy: <strong className="text-destructive">{seoJob.errors}</strong>
                </p>
              </div>
            )}

            {seoResult && seoResult.status === 'started' && (
              <div className="bg-primary/5 border border-primary/20 p-4" data-testid="seo-result">
                <p className="text-sm text-primary"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Zadanie uruchomione w tle. Możesz opuścić tę stronę.</p>
              </div>
            )}

            {seoStats?.history?.length > 0 && (
              <div>
                <h3 className="text-sm font-heading font-bold text-white mb-2">Historia generacji</h3>
                <div className="space-y-1">
                  {seoStats.history.map((h, i) => (
                    <div key={i} className="flex items-center justify-between text-xs py-1.5 border-b border-border/20 last:border-0">
                      <span className={h.type === 'generate' ? 'text-primary' : 'text-zorki'}>{h.type === 'generate' ? 'Generuj' : 'Regeneruj'}</span>
                      <span className="text-white">{h.processed}/{h.count}</span>
                      <span className="text-muted-foreground">{new Date(h.created_at).toLocaleString('pl-PL')}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </TabsContent>

        {/* Products Admin */}
        <TabsContent value="products">
          <div className="bg-card border border-border/40 p-6 space-y-4">
            <h2 className="font-heading text-lg font-bold text-white flex items-center gap-2">
              <Package className="w-5 h-5 text-primary" /> Zarządzanie produktami ({adminProductsTotal})
            </h2>
            <div className="flex flex-col sm:flex-row gap-2">
              <div className="flex gap-1">
                {[{v:'all',l:'Wszystkie'},{v:'with_seo',l:'Z SEO'},{v:'without_seo',l:'Bez SEO'}].map(f => (
                  <button key={f.v} onClick={() => { setAdminProductsFilter(f.v); setAdminProductsPage(1); setTimeout(fetchAdminProducts, 50); }}
                    className={`px-3 py-1 text-xs font-heading border ${adminProductsFilter === f.v ? 'border-primary text-primary bg-primary/10' : 'border-border/40 text-muted-foreground'}`}>{f.l}</button>
                ))}
              </div>
              <div className="flex gap-2 flex-1">
                <Input value={adminProductsSearch} onChange={e => setAdminProductsSearch(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && fetchAdminProducts()}
                  placeholder="Szukaj produktu..." className="bg-input border-transparent focus:border-primary rounded-none text-xs flex-1" />
                <Button onClick={fetchAdminProducts} className="bg-secondary text-secondary-foreground rounded-none px-3"><Search className="w-4 h-4" /></Button>
              </div>
            </div>
            <div className="space-y-1">
              {adminProducts.map(p => (
                <div key={p.id} className="flex items-center gap-3 py-2 border-b border-border/20 last:border-0">
                  <span className={`w-2 h-2 flex-shrink-0 ${p.has_seo ? 'bg-green-500' : 'bg-destructive'}`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-white truncate">{p.title}</p>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      {p.deal_price && <span className="text-primary">{p.deal_price} zł</span>}
                      <span>{p.category}</span>
                      {p.has_seo && <span className="text-green-400">SEO</span>}
                    </div>
                  </div>
                  <Link to={`/admin/product/${p.id}`} className="p-1.5 text-muted-foreground hover:text-primary transition-colors" data-testid={`edit-product-seo-${p.id}`}>
                    <Pencil className="w-4 h-4" />
                  </Link>
                  <a href={`/produkt/${p.id}`} target="_blank" rel="noopener noreferrer" className="p-1.5 text-muted-foreground hover:text-primary transition-colors">
                    <Eye className="w-4 h-4" />
                  </a>
                </div>
              ))}
              {adminProducts.length === 0 && <p className="text-sm text-muted-foreground text-center py-4">Kliknij filtr lub szukaj</p>}
            </div>
          </div>
        </TabsContent>

        {/* XML Import */}
        <TabsContent value="xml">
          <div className="bg-card border border-border/40 p-6 space-y-4">
            <h2 className="font-heading text-lg font-bold text-white flex items-center gap-2">
              <Upload className="w-5 h-5 text-primary" /> Import pliku XML
            </h2>
            <p className="text-sm text-muted-foreground">
              Prześlij plik XML lub podaj URL feeda (TradeDoubler, Ceneo, itp.). Linki afiliacyjne z productUrl/affiliate_url zostaną użyte automatycznie.
            </p>
            <div className="space-y-2">
              <div className="flex flex-col sm:flex-row gap-2">
                <Input
                  data-testid="xml-file-input"
                  type="file"
                  accept=".xml"
                  onChange={(e) => { setXmlFile(e.target.files[0]); setXmlUrl(''); }}
                  className="bg-input border-transparent focus:border-primary rounded-none flex-1"
                />
              </div>
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <span className="w-8 border-t border-border/40"></span> lub URL feeda <span className="w-8 border-t border-border/40"></span>
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <Input
                  data-testid="xml-url-input"
                  value={xmlUrl}
                  onChange={(e) => { setXmlUrl(e.target.value); setXmlFile(null); }}
                  placeholder="https://api.tradedoubler.com/1.0/products.xml..."
                  className="bg-input border-transparent focus:border-primary rounded-none flex-1 text-xs"
                />
              </div>
              <Button
                data-testid="xml-import-btn"
                onClick={handleXmlImport}
                disabled={importing || (!xmlFile && !xmlUrl)}
                className="bg-primary text-primary-foreground font-heading font-bold uppercase tracking-wider rounded-none w-full sm:w-auto"
              >
                {importing ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Importowanie...</> : 'Importuj XML'}
              </Button>
            </div>

            {importResult && (
              <div className="bg-secondary/50 border border-primary/20 p-4 mt-4" data-testid="import-result">
                <div className="flex items-center gap-2 text-primary mb-2">
                  <CheckCircle className="w-5 h-5" />
                  <span className="font-heading font-bold">Import zakończony!</span>
                </div>
                <p className="text-sm text-muted-foreground">
                  Nowe okazje: <strong className="text-white">{importResult.imported}</strong> |
                  Zaktualizowane: <strong className="text-white">{importResult.updated}</strong>
                </p>
              </div>
            )}

            {/* Import history */}
            {xmlImports.length > 0 && (
              <div className="mt-4">
                <h3 className="text-sm font-heading font-bold text-white mb-2">Historia importów</h3>
                <div className="space-y-2">
                  {xmlImports.map(imp => (
                    <div key={imp.id} className="flex items-center justify-between text-sm border-b border-border/20 py-2">
                      <span className="text-muted-foreground">{imp.filename}</span>
                      <div className="flex items-center gap-4">
                        <span className="text-white">{imp.deals_imported} nowych</span>
                        <span className="text-xs text-muted-foreground">
                          {new Date(imp.created_at).toLocaleDateString('pl-PL')}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </TabsContent>

        {/* Batch AI */}
        <TabsContent value="ai">
          <div className="bg-card border border-border/40 p-6 space-y-4">
            <h2 className="font-heading text-lg font-bold text-white flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-primary" /> Generowanie opisów AI (Grok)
            </h2>
            <p className="text-sm text-muted-foreground">
              Wygeneruj unikalne opisy AI dla okazji, które jeszcze ich nie mają. Grok API przetworzy każdą okazję osobno.
            </p>
            <div className="flex items-center gap-3">
              <Label className="text-sm text-muted-foreground whitespace-nowrap">Liczba produktów:</Label>
              <div className="flex gap-2">
                {[25, 50, 100].map(n => (
                  <button
                    key={n}
                    onClick={() => setBatchCount(n)}
                    className={`px-4 py-1.5 text-sm font-heading border transition-colors ${
                      batchCount === n ? 'border-primary text-primary bg-primary/10' : 'border-border/40 text-muted-foreground'
                    }`}
                  >
                    {n}
                  </button>
                ))}
              </div>
            </div>
            <Button
              data-testid="batch-ai-btn"
              onClick={handleBatchAi}
              disabled={batchLoading}
              className="bg-primary text-primary-foreground font-heading font-bold uppercase tracking-wider rounded-none"
            >
              {batchLoading ? (
                <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Generowanie...</>
              ) : (
                <><Sparkles className="w-4 h-4 mr-2" /> Generuj opisy dla {batchCount} produktów</>
              )}
            </Button>

            {batchResult && (
              <div className="bg-secondary/50 border border-primary/20 p-4" data-testid="batch-result">
                <div className="flex items-center gap-2 text-primary mb-2">
                  <CheckCircle className="w-5 h-5" />
                  <span className="font-heading font-bold">Batch zakończony!</span>
                </div>
                <p className="text-sm text-muted-foreground">
                  Przetworzono: <strong className="text-white">{batchResult.processed}</strong> |
                  Błędy: <strong className="text-destructive">{batchResult.errors}</strong>
                </p>
              </div>
            )}
          </div>
        </TabsContent>

        {/* Blog Management */}
        <TabsContent value="blog">
          <div className="bg-card border border-border/40 p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-lg font-bold text-white flex items-center gap-2">
                <FileText className="w-5 h-5 text-primary" /> Artykuły na blogu
              </h2>
              <Link
                to="/admin/blog/new"
                className="inline-flex items-center gap-1.5 bg-primary text-primary-foreground px-4 py-2 text-xs font-heading font-bold uppercase tracking-wider hover:bg-primary/90 transition-colors"
                data-testid="new-article-btn"
              >
                <Plus className="w-4 h-4" /> Nowy artykuł
              </Link>
            </div>
            <div className="space-y-2">
              {articles.length === 0 ? (
                <p className="text-sm text-muted-foreground py-4 text-center">Brak artykułów. Stwórz pierwszy!</p>
              ) : (
                articles.map(a => (
                  <div key={a.id} className="flex items-center justify-between py-3 border-b border-border/20 last:border-0">
                    <div className="flex-1 min-w-0 mr-4">
                      <p className="text-sm text-white font-medium truncate">{a.title}</p>
                      <div className="flex items-center gap-3 text-xs text-muted-foreground mt-0.5">
                        <span className={a.status === 'published' ? 'text-green-400' : 'text-yellow-400'}>
                          {a.status === 'published' ? 'Opublikowany' : 'Szkic'}
                        </span>
                        <span><Eye className="w-3 h-3 inline mr-0.5" />{a.views}</span>
                        <span>{new Date(a.created_at).toLocaleDateString('pl-PL')}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Link
                        to={`/admin/blog/edit/${a.id}`}
                        className="p-1.5 text-muted-foreground hover:text-primary transition-colors"
                        data-testid={`edit-article-${a.id}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </Link>
                      <button
                        onClick={() => deleteArticle(a.id)}
                        className="p-1.5 text-muted-foreground hover:text-destructive transition-colors"
                        data-testid={`delete-article-${a.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </TabsContent>

        {/* Settings */}
        <TabsContent value="settings">
          <div className="bg-card border border-border/40 p-6 space-y-6">
            <h2 className="font-heading text-lg font-bold text-white flex items-center gap-2">
              <Settings className="w-5 h-5 text-primary" /> Ustawienia
            </h2>

            {/* Grok API */}
            <div>
              <Label className="text-sm text-muted-foreground mb-1.5 block">Klucz API Grok (xAI)</Label>
              <div className="flex gap-2">
                <Input
                  data-testid="grok-key-input"
                  type="password"
                  value={grokKey}
                  onChange={(e) => setGrokKey(e.target.value)}
                  placeholder="xai-..."
                  className="bg-input border-transparent focus:border-primary rounded-none flex-1"
                />
                <Button
                  data-testid="save-grok-key-btn"
                  onClick={saveGrokKey}
                  disabled={!grokKey}
                  className="bg-primary text-primary-foreground rounded-none"
                >
                  Zapisz
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Nadpisuje domyślny klucz z .env. Używany do poprawy opisów i batch generowania.
              </p>
            </div>

            {/* Google AdSense */}
            <div className="border-t border-border/30 pt-4">
              <h3 className="font-heading text-base font-bold text-white mb-2">Google AdSense</h3>
              <div className="bg-secondary/50 border border-border/40 p-4">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span className="text-sm text-green-400 font-medium">Kod AdSense aktywny</span>
                </div>
                <p className="text-xs text-muted-foreground mb-2">
                  Publisher ID: <span className="font-mono text-white">ca-pub-8782302140557139</span>
                </p>
                <p className="text-xs text-muted-foreground">
                  Kod Google AdSense jest wstawiony w &lt;head&gt; i automatycznie wyświetla reklamy na:
                </p>
                <ul className="text-xs text-muted-foreground mt-1 space-y-0.5 ml-4">
                  <li className="list-disc">Feed Gadulec (co 5 okazji)</li>
                  <li className="list-disc">Strony artykułów na blogu (sidebar)</li>
                  <li className="list-disc">Strony szczegółów okazji</li>
                  <li className="list-disc">Automatyczne reklamy Google (wszystkie strony)</li>
                </ul>
              </div>
            </div>

            {/* SMTP Configuration */}
            <div className="border-t border-border/30 pt-4">
              <h3 className="font-heading text-base font-bold text-white mb-2">
                Konfiguracja SMTP (email)
                {smtpConfigured && <span className="text-green-400 text-xs ml-2 font-normal">Skonfigurowane</span>}
              </h3>
              <div className="grid grid-cols-2 gap-3 mb-3">
                <div>
                  <Label className="text-xs text-muted-foreground mb-1 block">Host SMTP</Label>
                  <Input value={smtpHost} onChange={e => setSmtpHost(e.target.value)} placeholder="smtp.gmail.com" className="bg-input border-transparent focus:border-primary rounded-none text-xs" data-testid="smtp-host" />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground mb-1 block">Port</Label>
                  <Input value={smtpPort} onChange={e => setSmtpPort(e.target.value)} placeholder="587" className="bg-input border-transparent focus:border-primary rounded-none text-xs" data-testid="smtp-port" />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground mb-1 block">Email (login)</Label>
                  <Input value={smtpUser} onChange={e => setSmtpUser(e.target.value)} placeholder="noreply@znajdownik.pl" className="bg-input border-transparent focus:border-primary rounded-none text-xs" data-testid="smtp-user" />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground mb-1 block">Hasło</Label>
                  <Input type="password" value={smtpPass} onChange={e => setSmtpPass(e.target.value)} placeholder="hasło aplikacji" className="bg-input border-transparent focus:border-primary rounded-none text-xs" data-testid="smtp-pass" />
                </div>
              </div>
              <div className="flex gap-2">
                <Button onClick={saveSmtpConfig} disabled={smtpSaving} className="bg-primary text-primary-foreground rounded-none text-xs" data-testid="save-smtp-btn">
                  {smtpSaving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null} Zapisz SMTP
                </Button>
                {smtpConfigured && (
                  <div className="flex gap-2 flex-1">
                    <Input value={smtpTestEmail} onChange={e => setSmtpTestEmail(e.target.value)} placeholder="email@test.pl" className="bg-input border-transparent focus:border-primary rounded-none text-xs" data-testid="smtp-test-email" />
                    <Button onClick={testSmtp} className="bg-secondary text-secondary-foreground rounded-none text-xs" data-testid="smtp-test-btn">Test</Button>
                  </div>
                )}
              </div>
            </div>
            {/* Epidemos API */}
            <div className="border-t border-border/30 pt-4">
              <h3 className="font-heading text-base font-bold text-white mb-2">Epidemos External API</h3>
              <p className="text-xs text-muted-foreground mb-2">Klucz API do publikacji przez zewnętrzny system Epidemos. Można podać wiele kluczy oddzielonych przecinkiem.</p>
              <div className="flex gap-2 mb-2">
                <Input value={epidemosKey} onChange={e => setEpidemosKey(e.target.value)} placeholder="epidemos-api-key-xxx" className="bg-input border-transparent focus:border-primary rounded-none text-xs flex-1" data-testid="epidemos-key-input" />
                <Button onClick={async () => {
                  if (!epidemosKey) return;
                  try {
                    const fd = new FormData(); fd.append('key','epidemos_api_key'); fd.append('value',epidemosKey);
                    await axios.post(`${API}/admin/settings`, fd, { headers: authHeaders() });
                    toast.success('Klucz Epidemos zapisany'); setEpidemosKey('');
                  } catch { toast.error('Błąd'); }
                }} className="bg-primary text-primary-foreground rounded-none text-xs" data-testid="save-epidemos-key">Zapisz</Button>
              </div>
              <div className="text-xs text-muted-foreground space-y-1 mt-2">
                <p className="font-heading text-white text-sm mb-1">Endpointy API (header: X-API-Key):</p>
                <p className="font-mono text-white/60">POST /api/external/publish</p>
                <p className="text-muted-foreground/70 ml-4">Pola: title*, content*, description, tags (przecinki), category, deal_price, original_price, url, affiliate_url, author_name, image (plik), image_url</p>
                <p className="font-mono text-white/60">PUT /api/external/publish/SLUG</p>
                <p className="text-muted-foreground/70 ml-4">Aktualizacja wpisu - te same pola co POST</p>
                <p className="font-mono text-white/60">DELETE /api/external/publish/SLUG</p>
                <p className="text-muted-foreground/70 ml-4">Usunięcie wpisu z komentarzami i ocenami</p>
                <p className="font-mono text-white/60">POST /api/external/publish/SLUG/comment</p>
                <p className="text-muted-foreground/70 ml-4">Pola: text*, author_name</p>
                <p className="font-mono text-white/60">POST /api/external/publish/SLUG/rate</p>
                <p className="text-muted-foreground/70 ml-4">Pola: rating* (hot/cold/meh), author_name</p>
                <p className="font-mono text-white/60">GET /api/external/posts</p>
                <p className="text-muted-foreground/70 ml-4">Lista wpisów z komentarzami i reakcjami. Params: page, limit, source</p>
                <p className="font-mono text-white/60">GET /api/external/posts/SLUG</p>
                <p className="text-muted-foreground/70 ml-4">Pojedynczy wpis z pełnymi komentarzami i reakcjami</p>
              </div>
            </div>

            {/* Backfill slugs */}
            <div className="border-t border-border/30 pt-4">
              <h3 className="font-heading text-base font-bold text-white mb-2">Przyjazne linki (slugi)</h3>
              <p className="text-xs text-muted-foreground mb-2">Wygeneruj slug URL dla starszych produktów bez przyjaznych linków.</p>
              <Button onClick={async () => {
                try {
                  const res = await axios.post(`${API}/admin/backfill-slugs`, null, { headers: authHeaders() });
                  toast.success(`Zaktualizowano ${res.data.updated} produktów`);
                } catch { toast.error('Błąd'); }
              }} className="bg-secondary text-secondary-foreground rounded-none text-xs" data-testid="backfill-slugs-btn">
                Generuj slugi dla starych produktów
              </Button>
            </div>
          </div>
        </TabsContent>

        {/* Users */}
        <TabsContent value="users">
          <div className="bg-card border border-border/40 p-6">
            <h2 className="font-heading text-lg font-bold text-white flex items-center gap-2 mb-4">
              <Users className="w-5 h-5 text-primary" /> Ostatni użytkownicy
            </h2>
            <div className="space-y-2">
              {stats?.recent_users?.map(u => (
                <div key={u.id} className="flex items-center justify-between py-2 border-b border-border/20 last:border-0">
                  <div>
                    <span className="text-sm font-medium text-white">{u.nick}</span>
                    <span className="text-xs text-muted-foreground ml-2">{u.email || u.phone || ''}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-zorki font-heading font-bold">{u.zorki} Z</span>
                    <span className={`text-xs px-2 py-0.5 ${u.role === 'admin' ? 'bg-primary/20 text-primary' : 'bg-secondary text-muted-foreground'}`}>
                      {u.role}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
